import { resources, type LanguageCode } from '../locales';
import { getA11yFallback } from '../locales/a11yFallback';
import { mergeResource } from '../locales/mergeResource';
import type { LocaleResource } from '../locales/types';
import { I18N_DEFAULT_LANGUAGE } from './config';

type TranslateOptions = Record<string, string | number>;

let activeLanguage: LanguageCode = I18N_DEFAULT_LANGUAGE;

function withA11yResource(base: LocaleResource, code: LanguageCode): LocaleResource {
  return mergeResource(base, { a11y: getA11yFallback(code) }) as LocaleResource;
}

let activeResource: LocaleResource = withA11yResource(
  (resources[I18N_DEFAULT_LANGUAGE]?.translation ?? {}) as LocaleResource,
  I18N_DEFAULT_LANGUAGE,
);

const overlayCache = new Map<LanguageCode, Partial<LocaleResource>>();

const resolvePath = (source: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (current && typeof current === 'object' && segment in current) {
      return (current as Record<string, unknown>)[segment];
    }
    return undefined;
  }, source);
};

const applyInterpolation = (template: string, options?: TranslateOptions) => {
  if (!options) return template;
  return Object.entries(options).reduce(
    (result, [key, value]) => result.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value)),
    template,
  );
};

async function loadOverlay(code: LanguageCode): Promise<Partial<LocaleResource>> {
  if (overlayCache.has(code)) return overlayCache.get(code)!;
  try {
    const mod = await import(`../locales/overlays/${code}.ts`);
    const overlay = (mod.overlay ??
      mod[`overlay${code.charAt(0).toUpperCase()}${code.slice(1)}`]) as Partial<LocaleResource>;
    overlayCache.set(code, overlay);
    return overlay;
  } catch (e) {
    console.error(`[i18n] Failed to load overlay for language "${code}":`, e);
    overlayCache.set(code, {});
    return {};
  }
}

export function setActiveLanguage(code: LanguageCode, resource?: LocaleResource) {
  activeLanguage = code;
  const base = (resource ??
    resources[code]?.translation ??
    resources[I18N_DEFAULT_LANGUAGE].translation) as LocaleResource;
  const overlay = overlayCache.get(code) ?? {};
  activeResource = withA11yResource(mergeResource(base, overlay) as LocaleResource, code);
}

export async function hydrateLanguage(code: LanguageCode) {
  const overlay = await loadOverlay(code);
  const base = (resources[code]?.translation ??
    resources[I18N_DEFAULT_LANGUAGE].translation) as LocaleResource;
  activeLanguage = code;
  activeResource = withA11yResource(mergeResource(base, overlay) as LocaleResource, code);
}

export function getActiveLanguage(): LanguageCode {
  return activeLanguage;
}

export function translate(key: string, options?: TranslateOptions): string {
  const value = resolvePath(activeResource as Record<string, unknown>, key);
  if (typeof value === 'string') return applyInterpolation(value, options);

  // Fallback to default language if key not found in active language
  if (activeLanguage !== I18N_DEFAULT_LANGUAGE) {
    const fallbackResource = resources[I18N_DEFAULT_LANGUAGE].translation;
    const fallbackValue = resolvePath(fallbackResource as Record<string, unknown>, key);
    if (typeof fallbackValue === 'string') return applyInterpolation(fallbackValue, options);
  }

  if (key.startsWith('a11y.')) {
    const fallback = getA11yFallback(activeLanguage);
    const fallbackValue = resolvePath(fallback as Record<string, unknown>, key.slice(5));
    if (typeof fallbackValue === 'string') return applyInterpolation(fallbackValue, options);
  }

  // Support a { defaultValue: '...' } fallback option
  if (options && typeof options['defaultValue'] === 'string') {
    return options['defaultValue'];
  }

  return key;
}
